import React, { useEffect, useRef, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { FaProjectDiagram, FaPlay, FaPause, FaExpand, FaTimes, FaArrowRight, FaLink, FaEye, FaEyeSlash } from 'react-icons/fa';
import {
  TechSection,
  TechButtonGhost,
  Empty,
  ErrorBox,
  borderSoft,
  mono,
  accent,
} from '../../styles/techComponents';
import { getAiLiveGraph } from '../../services/aiLiveService';
import friendlyError from '../../utils/friendlyError';

const Intro = styled.p`
  margin: 0 0 0.9rem;
  font-size: 0.9rem;
  line-height: 1.55;
  color: var(--text-secondary);

  strong { color: var(--text-primary); font-weight: 600; }
`;

const GraphShell = styled.div`
  position: relative;
  height: ${(p) => (p.$fullscreen ? '100vh' : '620px')};
  border: 1px solid ${borderSoft};
  border-radius: 6px;
  overflow: hidden;
  background: radial-gradient(ellipse at 50% 40%, #0d1420 0%, #070b12 65%, #05080d 100%);

  ${(p) => p.$fullscreen && `
    position: fixed;
    inset: 0;
    z-index: 1000;
    border-radius: 0;
    border: none;
  `}

  canvas { display: block; }
`;

const Overlay = styled.div`
  position: absolute;
  pointer-events: none;
  font-family: ${mono};
  font-size: 0.62rem;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--text-disabled);
`;

const Legend = styled(Overlay)`
  left: 12px;
  bottom: 12px;
  display: flex;
  flex-direction: column;
  gap: 5px;
  background: rgba(7, 11, 18, 0.7);
  border: 1px solid ${borderSoft};
  border-radius: 5px;
  padding: 8px 11px;
  backdrop-filter: blur(3px);

  .item { display: flex; align-items: center; gap: 7px; }
  .swatch { width: 9px; height: 9px; border-radius: 50%; flex-shrink: 0; }
  .line { width: 14px; height: 2px; flex-shrink: 0; }
`;

const StatsChip = styled(Overlay)`
  top: 12px;
  right: 12px;
  text-align: right;
  background: rgba(7, 11, 18, 0.7);
  border: 1px solid ${borderSoft};
  border-radius: 5px;
  padding: 8px 11px;
  backdrop-filter: blur(3px);
  line-height: 1.7;

  strong { color: var(--text-primary); font-weight: 600; }
`;

const Hint = styled(Overlay)`
  left: 12px;
  top: 12px;
  background: rgba(7, 11, 18, 0.55);
  border-radius: 5px;
  padding: 6px 9px;
`;

const FullscreenBtns = styled.div`
  position: absolute;
  right: 12px;
  bottom: 12px;
  display: flex;
  gap: 6px;
`;

// Panel de detalle que aparece al hacer click en un nodo
const DetailPanel = styled.div`
  position: absolute;
  top: 12px;
  left: 12px;
  width: 300px;
  max-width: calc(100% - 24px);
  max-height: calc(100% - 24px);
  overflow-y: auto;
  background: rgba(9, 13, 20, 0.92);
  border: 1px solid ${borderSoft};
  border-left: 3px solid ${(p) => p.$color || accent};
  border-radius: 6px;
  padding: 14px 16px 16px;
  backdrop-filter: blur(6px);
  z-index: 5;

  .kicker {
    font-family: ${mono};
    font-size: 0.58rem;
    letter-spacing: 0.18em;
    text-transform: uppercase;
    color: ${(p) => p.$color || accent};
  }

  h4 {
    margin: 4px 0 0;
    font-size: 1.05rem;
    color: var(--text-primary);
    line-height: 1.25;
  }

  .raw {
    font-family: ${mono};
    font-size: 0.62rem;
    color: var(--text-disabled);
    margin-top: 3px;
  }

  .desc {
    margin: 10px 0 0;
    font-size: 0.84rem;
    line-height: 1.5;
    color: var(--text-secondary);
  }

  .metrics {
    display: flex;
    gap: 14px;
    margin-top: 12px;
    padding-top: 12px;
    border-top: 1px solid ${borderSoft};
  }

  .metric .n {
    font-family: ${mono};
    font-size: 1.15rem;
    font-weight: 600;
    color: var(--text-primary);
  }

  .metric .l {
    font-size: 0.6rem;
    text-transform: uppercase;
    letter-spacing: 0.1em;
    color: var(--text-disabled);
    margin-top: 2px;
  }

  .block { margin-top: 12px; }

  .block-title {
    font-family: ${mono};
    font-size: 0.58rem;
    letter-spacing: 0.16em;
    text-transform: uppercase;
    color: var(--text-disabled);
    margin-bottom: 6px;
  }

  .chips { display: flex; flex-wrap: wrap; gap: 5px; }

  .chip {
    font-size: 0.72rem;
    padding: 2px 8px;
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.05);
    border: 1px solid ${borderSoft};
    color: var(--text-secondary);
    display: inline-flex;
    align-items: center;
    gap: 4px;
    cursor: ${(p) => (p.$clickableChips ? 'pointer' : 'default')};
  }

  .open-btn {
    margin-top: 14px;
    width: 100%;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 7px;
    padding: 9px 12px;
    background: ${(p) => p.$color || accent};
    color: #06121a;
    font-weight: 600;
    font-size: 0.85rem;
    border: none;
    border-radius: 5px;
    cursor: pointer;
    transition: filter 0.15s ease;
    &:hover { filter: brightness(1.1); }
  }

  .close {
    position: absolute;
    top: 10px;
    right: 10px;
    background: none;
    border: none;
    color: var(--text-disabled);
    cursor: pointer;
    font-size: 0.85rem;
    &:hover { color: var(--text-primary); }
  }
`;

function fmtRows(n) {
  if (n == null) return '0';
  return Number(n).toLocaleString('es-AR');
}

const PlatformGraph3D = () => {
  const navigate = useNavigate();
  const containerRef = useRef(null);
  const graphRef = useRef(null);
  const cloudRef = useRef(null);
  const clickCleanupRef = useRef(null);
  const frameTimeoutRef = useRef(null);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [rotating, setRotating] = useState(true);
  const [fullscreen, setFullscreen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [showRecords, setShowRecords] = useState(true);

  useEffect(() => {
    let cancelled = false;
    getAiLiveGraph()
      .then((d) => { if (!cancelled) setData(d); })
      .catch((err) => {
        if (!cancelled) setError(friendlyError(err));
      });
    return () => { cancelled = true; };
  }, []);

  // Índices para el panel de detalle: nombre por id, y relaciones (FK) de cada tabla
  const nodeById = useMemo(() => {
    const m = new Map();
    (data?.nodes || []).forEach((n) => m.set(n.id, n));
    return m;
  }, [data]);

  const relationsByNode = useMemo(() => {
    const m = new Map();
    const idOf = (x) => (typeof x === 'object' ? x.id : x);
    (data?.links || []).forEach((l) => {
      if (l.type !== 'fk') return;
      const s = idOf(l.source);
      const t = idOf(l.target);
      if (!m.has(s)) m.set(s, new Set());
      if (!m.has(t)) m.set(t, new Set());
      m.get(s).add(t);
      m.get(t).add(s);
    });
    return m;
  }, [data]);

  // Inicializa el grafo 3D cuando llegan los datos (import dinámico para
  // no meter three.js en el bundle principal).
  useEffect(() => {
    if (!data || !containerRef.current) return undefined;
    let disposed = false;

    (async () => {
      const [{ default: ForceGraph3D }, { default: SpriteText }, THREE] = await Promise.all([
        import('3d-force-graph'),
        import('three-spritetext'),
        import('three'),
      ]);
      if (disposed || !containerRef.current) return;

      const el = containerRef.current;
      const nodes = data.nodes.map((n) => ({ ...n }));
      const links = data.links.map((l) => ({ ...l }));
      const colorById = new Map(nodes.map((n) => [n.id, n.color]));
      const srcId = (l) => (typeof l.source === 'object' ? l.source.id : l.source);

      const graph = new ForceGraph3D(el)
        .width(el.clientWidth)
        .height(el.clientHeight)
        .backgroundColor('rgba(0,0,0,0)')
        .showNavInfo(false)
        .nodeVal((n) => {
          if (n.type === 'core') return 90;
          if (n.type === 'section') return 14 + (n.tables || 0) * 2;
          return 1.5 + Math.log10((n.rows || 0) + 1) * 2.2;
        })
        .nodeColor((n) => n.color)
        .nodeOpacity(0.9)
        .nodeLabel((n) => {
          if (n.type === 'core') return `<div style="font-family:sans-serif;font-size:12px;padding:3px 5px;max-width:220px;"><b>${n.label}</b></div>`;
          if (n.type === 'section') {
            return `<div style="font-family:sans-serif;font-size:12px;padding:3px 5px;max-width:240px;">`
              + `<b>${n.label}</b> · sección<br/><span style="opacity:.85">${n.desc || ''}</span><br/>`
              + `<span style="opacity:.6">${n.tables || 0} tipos de dato · ${fmtRows(n.rows)} registros · click para abrir</span></div>`;
          }
          return `<div style="font-family:sans-serif;font-size:12px;padding:3px 5px;max-width:240px;">`
            + `<b>${n.label}</b><br/><span style="opacity:.85">${n.desc || ''}</span><br/>`
            + `<span style="opacity:.6">${fmtRows(n.rows)} registros · click para ver detalle</span></div>`;
        })
        .nodeThreeObject((n) => {
          if (n.type === 'table') return undefined; // esfera por defecto
          const sprite = new SpriteText(n.label);
          sprite.color = n.type === 'core' ? '#ffffff' : n.color;
          sprite.textHeight = n.type === 'core' ? 11 : 5.2;
          sprite.fontWeight = '600';
          sprite.fontFace = 'Arial';
          sprite.backgroundColor = 'rgba(8, 12, 19, 0.65)';
          sprite.padding = n.type === 'core' ? 6 : 3;
          sprite.borderRadius = 4;
          sprite.material.depthWrite = false;
          return sprite;
        })
        .linkColor((l) => {
          if (l.type === 'fk') return '#00f5d4';
          if (l.type === 'core') return '#6c7a91';
          if (l.type === 'orphan') return '#2c3442';
          return colorById.get(srcId(l)) || '#00b4d8';
        })
        .linkOpacity(0.3)
        .linkWidth((l) => (l.type === 'fk' ? 0.8 : 0))
        .linkDirectionalParticles((l) => (l.type === 'fk' ? 2 : 0))
        .linkDirectionalParticleWidth(1.5)
        .linkDirectionalParticleSpeed(0.006)
        .graphData({ nodes, links });

      // Selección de nodos con raycaster propio. El sistema de click interno de
      // 3d-force-graph no dispara de forma fiable en este entorno; escuchamos el
      // 'click' nativo del canvas y hacemos el raycast contra los nodos nosotros.
      const focusOnNode = (n) => {
        if (!n || n.type === 'core') { setSelected(null); return; }
        setSelected(n);
        const dist = n.type === 'section' ? 150 : 110;
        const len = Math.hypot(n.x || 1, n.y || 1, n.z || 1) || 1;
        const ratio = 1 + dist / len;
        graph.cameraPosition(
          { x: (n.x || 0) * ratio, y: (n.y || 0) * ratio, z: (n.z || 0) * ratio },
          n,
          900
        );
      };

      // Detección del nodo bajo el click por proyección inversa: proyectamos el
      // centro de cada nodo a la pantalla y elegimos el más cercano al click.
      // (El raycaster no acierta de forma fiable en este entorno.)
      const canvasEl = graph.renderer().domElement;
      const tmpVec = new THREE.Vector3();
      let downX = 0;
      let downY = 0;
      const onDown = (ev) => { downX = ev.clientX; downY = ev.clientY; };
      const pickNodeAt = (cx, cy) => {
        const rect = canvasEl.getBoundingClientRect();
        const cam = graph.camera();
        let best = null;
        let bestDist = Infinity;
        for (const n of graph.graphData().nodes) {
          const obj = n.__threeObj;
          if (!obj) continue;
          obj.getWorldPosition(tmpVec);
          tmpVec.project(cam);
          if (tmpVec.z > 1) continue; // fuera del frustum / detrás de cámara
          const sx = rect.left + (tmpVec.x * 0.5 + 0.5) * rect.width;
          const sy = rect.top + (-tmpVec.y * 0.5 + 0.5) * rect.height;
          const d = Math.hypot(cx - sx, cy - sy);
          const tol = n.type === 'section' ? 45 : n.type === 'core' ? 55 : 28;
          if (d < tol && d < bestDist) { bestDist = d; best = n; }
        }
        return best;
      };
      const onClick = (ev) => {
        // ignorar si fue un arrastre (rotación de cámara)
        if (Math.hypot(ev.clientX - downX, ev.clientY - downY) > 5) return;
        focusOnNode(pickNodeAt(ev.clientX, ev.clientY));
      };
      canvasEl.addEventListener('pointerdown', onDown);
      canvasEl.addEventListener('click', onClick);
      clickCleanupRef.current = () => {
        canvasEl.removeEventListener('pointerdown', onDown);
        canvasEl.removeEventListener('click', onClick);
      };

      graph.d3Force('link').distance((l) => {
        if (l.type === 'core') return 160;
        if (l.type === 'fk') return 55;
        if (l.type === 'orphan') return 220;
        return 38;
      });
      graph.d3Force('charge').strength(-140);

      const controls = graph.controls();
      controls.autoRotate = true;
      controls.autoRotateSpeed = 0.6;

      // Encuadre inicial ya alejado: evita el instante en que la cámara arranca
      // "adentro" del cúmulo antes de que el layout se asiente.
      graph.cameraPosition({ x: 0, y: 0, z: 480 });

      // Nube de registros: un punto por cada fila real de la base (~192k).
      // Se construye una vez, cuando el layout se asienta y los nodos tienen
      // posición final, agrupando los puntos alrededor de su tipo de dato.
      const MAX_POINTS = 260000; // techo defensivo de rendimiento
      const hexToRgb = (hex) => {
        const h = hex.replace('#', '');
        return [
          parseInt(h.slice(0, 2), 16) / 255,
          parseInt(h.slice(2, 4), 16) / 255,
          parseInt(h.slice(4, 6), 16) / 255,
        ];
      };

      const buildRecordCloud = () => {
        if (cloudRef.current) return;
        const tableNodes = graph.graphData().nodes.filter(
          (n) => n.type === 'table' && (n.rows || 0) > 0
        );
        const totalRows = tableNodes.reduce((a, n) => a + (n.rows || 0), 0);
        if (!totalRows) return;
        const scale = totalRows > MAX_POINTS ? MAX_POINTS / totalRows : 1;

        const counts = tableNodes.map((n) => Math.max(1, Math.round((n.rows || 0) * scale)));
        const total = counts.reduce((a, b) => a + b, 0);
        const positions = new Float32Array(total * 3);
        const colors = new Float32Array(total * 3);

        let k = 0;
        tableNodes.forEach((n, ni) => {
          const count = counts[ni];
          const [cr, cg, cb] = hexToRgb(n.color || '#5c677d');
          const radius = 4 + Math.log10((n.rows || 0) + 1) * 4;
          const nx = n.x || 0;
          const ny = n.y || 0;
          const nz = n.z || 0;
          for (let i = 0; i < count; i++) {
            // dirección uniforme sobre la esfera + radio con concentración al centro
            const u = Math.random() * 2 - 1;
            const th = Math.random() * Math.PI * 2;
            const s = Math.sqrt(1 - u * u);
            const rr = radius * (0.35 + 0.65 * Math.cbrt(Math.random()));
            positions[k * 3] = nx + s * Math.cos(th) * rr;
            positions[k * 3 + 1] = ny + s * Math.sin(th) * rr;
            positions[k * 3 + 2] = nz + u * rr;
            const b = 0.7 + Math.random() * 0.5; // leve variación de brillo
            colors[k * 3] = Math.min(1, cr * b);
            colors[k * 3 + 1] = Math.min(1, cg * b);
            colors[k * 3 + 2] = Math.min(1, cb * b);
            k++;
          }
        });

        const geom = new THREE.BufferGeometry();
        geom.setAttribute('position', new THREE.BufferAttribute(positions, 3));
        geom.setAttribute('color', new THREE.BufferAttribute(colors, 3));
        const mat = new THREE.PointsMaterial({
          size: 1.1,
          sizeAttenuation: true,
          vertexColors: true,
          transparent: true,
          opacity: 0.55,
          depthWrite: false,
          blending: THREE.AdditiveBlending,
        });
        const points = new THREE.Points(geom, mat);
        points.visible = showRecords;
        points.renderOrder = -1; // detrás de esferas y etiquetas
        points.raycast = () => {}; // la nube no intercepta clicks; pasan al nodo
        cloudRef.current = points;
        graph.scene().add(points);
      };

      let framed = false;
      const frameGraph = () => {
        if (framed || disposed) return;
        framed = true;
        buildRecordCloud();
        graph.zoomToFit(700, 60);
      };
      graph.onEngineStop(frameGraph);
      // Respaldo: si el motor tarda o no emite el evento, encuadrá igual.
      frameTimeoutRef.current = setTimeout(frameGraph, 5000);

      graphRef.current = graph;
    })();

    return () => {
      disposed = true;
      if (frameTimeoutRef.current) {
        clearTimeout(frameTimeoutRef.current);
        frameTimeoutRef.current = null;
      }
      if (clickCleanupRef.current) {
        clickCleanupRef.current();
        clickCleanupRef.current = null;
      }
      if (cloudRef.current) {
        cloudRef.current.geometry?.dispose();
        cloudRef.current.material?.dispose();
        cloudRef.current = null;
      }
      if (graphRef.current) {
        graphRef.current._destructor?.();
        graphRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  // Mostrar/ocultar la nube de registros sin reconstruirla
  useEffect(() => {
    if (cloudRef.current) cloudRef.current.visible = showRecords;
  }, [showRecords]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(() => {
      if (graphRef.current && el.clientWidth > 0) {
        graphRef.current.width(el.clientWidth).height(el.clientHeight);
      }
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [data]);

  useEffect(() => {
    if (!fullscreen) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setFullscreen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [fullscreen]);

  const toggleRotate = () => {
    const graph = graphRef.current;
    if (graph) graph.controls().autoRotate = !rotating;
    setRotating((v) => !v);
  };

  // Enfoca un nodo por id (desde los chips de relaciones del panel)
  const focusNode = (id) => {
    const graph = graphRef.current;
    const n = graph?.graphData().nodes.find((x) => x.id === id);
    if (!n) return;
    setSelected(nodeById.get(id) || n);
    const len = Math.hypot(n.x || 1, n.y || 1, n.z || 1);
    const ratio = 1 + 110 / len;
    graph.cameraPosition({ x: n.x * ratio, y: n.y * ratio, z: n.z * ratio }, n, 900);
  };

  const stats = data?.stats;

  // Datos derivados del nodo seleccionado para el panel
  const relations = selected
    ? [...(relationsByNode.get(selected.id) || [])]
        .map((id) => nodeById.get(id))
        .filter(Boolean)
    : [];

  return (
    <TechSection
      title="Mapa de inteligencia de la campaña"
      icon={<FaProjectDiagram style={{ color: 'var(--text-disabled)', fontSize: '0.8rem' }} />}
      action={
        <div style={{ display: 'inline-flex', gap: 6 }}>
          <TechButtonGhost
            type="button"
            onClick={() => setShowRecords((v) => !v)}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
          >
            {showRecords ? <FaEyeSlash /> : <FaEye />}
            {showRecords ? 'Ocultar registros' : 'Ver registros'}
          </TechButtonGhost>
          <TechButtonGhost
            type="button"
            onClick={toggleRotate}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
          >
            {rotating ? <FaPause /> : <FaPlay />}
            {rotating ? 'Pausar rotación' : 'Rotar'}
          </TechButtonGhost>
        </div>
      }
      style={{ marginBottom: '1.25rem' }}
    >
      <Intro>
        Cada esfera es un <strong>tipo de información</strong> que la plataforma reúne sola
        (notas de prensa, reclamos de vecinos, expedientes del Concejo, redes…), y la
        <strong> nube de puntos que la rodea son sus registros reales</strong>: cada punto es
        una fila de la base ({data?.stats ? fmtRows(data.stats.total_rows) : '—'} en total).
        Las esferas se agrupan alrededor de la <strong>sección</strong> que las usa y las líneas
        turquesa muestran cómo un dato se conecta con otro. <strong>Tocá cualquier esfera</strong> para
        ver qué guarda y para qué sirve; tocá una sección para abrirla.
      </Intro>
      {error && <ErrorBox>No se pudo cargar el mapa · {error}</ErrorBox>}
      {!error && !data && <Empty>Cargando mapa de datos…</Empty>}
      {!error && data && (
        <GraphShell $fullscreen={fullscreen}>
          <div ref={containerRef} style={{ position: 'absolute', inset: 0 }} />
          {!selected && (
            <Hint>Arrastrá para rotar · scroll para zoom · tocá una esfera para ver qué es</Hint>
          )}
          {stats && (
            <StatsChip>
              <strong>{stats.sections}</strong> secciones ·{' '}
              <strong>{stats.tables}</strong> tipos de dato<br />
              <strong>{stats.fk_links}</strong> conexiones ·{' '}
              <strong>{fmtRows(stats.total_rows)}</strong> registros
            </StatsChip>
          )}

          {selected && (
            <DetailPanel $color={selected.color} $clickableChips>
              <button className="close" type="button" onClick={() => setSelected(null)}>
                <FaTimes />
              </button>
              <div className="kicker">
                {selected.type === 'section' ? 'Sección de la plataforma' : 'Tipo de información'}
              </div>
              <h4>{selected.label}</h4>
              {selected.type === 'table' && selected.raw && (
                <div className="raw">tabla · {selected.raw}</div>
              )}
              <p className="desc">{selected.desc}</p>

              <div className="metrics">
                <div className="metric">
                  <div className="n">{fmtRows(selected.rows)}</div>
                  <div className="l">registros</div>
                </div>
                {selected.type === 'section' && (
                  <div className="metric">
                    <div className="n">{selected.tables || 0}</div>
                    <div className="l">tipos de dato</div>
                  </div>
                )}
                {selected.type === 'table' && (
                  <div className="metric">
                    <div className="n">{relations.length}</div>
                    <div className="l">conexiones</div>
                  </div>
                )}
              </div>

              {selected.type === 'table' && selected.sections?.length > 0 && (
                <div className="block">
                  <div className="block-title">Se usa en</div>
                  <div className="chips">
                    {selected.sections.map((s) => (
                      <span className="chip" key={s}>{s}</span>
                    ))}
                  </div>
                </div>
              )}

              {selected.type === 'table' && relations.length > 0 && (
                <div className="block">
                  <div className="block-title">Se conecta con</div>
                  <div className="chips">
                    {relations.map((r) => (
                      <span
                        className="chip"
                        key={r.id}
                        onClick={() => focusNode(r.id)}
                        title="Ver en el mapa"
                      >
                        <FaLink style={{ fontSize: '0.6rem', opacity: 0.6 }} /> {r.label}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {selected.type === 'section' && selected.route && (
                <button
                  className="open-btn"
                  type="button"
                  onClick={() => navigate(selected.route)}
                >
                  Abrir sección <FaArrowRight />
                </button>
              )}
            </DetailPanel>
          )}

          <Legend>
            <div className="item"><span className="swatch" style={{ background: '#fff' }} /> Núcleo / secciones</div>
            <div className="item"><span className="swatch" style={{ background: '#00b4d8' }} /> Tipo de dato (tamaño = volumen)</div>
            <div className="item"><span className="swatch" style={{ background: '#00b4d8', width: 4, height: 4, boxShadow: '0 0 3px #00b4d8' }} /> Cada punto = 1 registro real</div>
            <div className="item"><span className="line" style={{ background: '#00f5d4' }} /> Conexión entre datos</div>
            <div className="item"><span className="line" style={{ background: '#6c7a91' }} /> Sección ↔ sus datos</div>
          </Legend>
          <FullscreenBtns>
            <TechButtonGhost
              type="button"
              onClick={() => setFullscreen((v) => !v)}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6, pointerEvents: 'auto' }}
            >
              <FaExpand /> {fullscreen ? 'Salir (ESC)' : 'Pantalla completa'}
            </TechButtonGhost>
          </FullscreenBtns>
        </GraphShell>
      )}
    </TechSection>
  );
};

export default PlatformGraph3D;
